import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ADMIN_API_PREFIX, BASE_URL } from '@CONSTANTS/API.constant';
import userRepository from '@REPOSITORIES/local-repository/user-repository';
import {
    Attachment,
    AttachmentListResponse,
    CreateAttachmentParams,
    UpdateAttachmentParams,
    ReorderAttachmentsParams,
    AttachmentQueryParams,
    DocumentExhibit,
} from '@INTERFACES/api/attachments';

export const attachmentsAdminApi = createApi({
    reducerPath: 'attachments-admin-api',
    baseQuery: fetchBaseQuery({
        baseUrl: BASE_URL + ADMIN_API_PREFIX,
        prepareHeaders: (headers) => {
            const token = userRepository.getData();

            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }

            return headers;
        },
    }),
    tagTypes: ['Attachment'],
    endpoints: (builder) => ({
        // Get attachments with optional filtering
        getAttachments: builder.query<Attachment[], AttachmentQueryParams>({
            query: (params = {}) => {
                const searchParams = new URLSearchParams();
                
                if (params.content_type) {
                    searchParams.append('content_type', params.content_type);
                }
                if (params.object_id) {
                    searchParams.append('object_id', params.object_id.toString());
                }
                if (params.document_id) {
                    searchParams.append('document_id', params.document_id.toString());
                }

                return {
                    url: `/attachments/?${searchParams.toString()}`,
                    method: 'GET',
                };
            },
            providesTags: (result) => (result
                ? [
                    ...(result as unknown as Attachment[]).map(({ id }) => ({ type: 'Attachment' as const, id })),
                    { type: 'Attachment', id: 'LIST' },
                ]
                : [{ type: 'Attachment', id: 'LIST' }]),
        }),

        // Get single attachment
        getAttachmentById: builder.query<Attachment, number>({
            query: (id) => ({
                url: `/attachments/${id}/`,
                method: 'GET',
            }),
            providesTags: (result, error, id) => [{ type: 'Attachment', id }],
        }),

        // Upload new attachment
        createAttachment: builder.mutation<Attachment, CreateAttachmentParams>({
            query: (params) => {
                const formData = new FormData();
                
                formData.append('name', params.name);
                formData.append('file', params.file);
                formData.append('content_type_name', params.content_type_name);
                formData.append('object_id', params.object_id.toString());
               
                return {
                    url: '/attachments/',
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: [{ type: 'Attachment', id: 'LIST' }],
        }),

        // Update attachment
        updateAttachment: builder.mutation<Attachment, UpdateAttachmentParams>({
            query: ({ id, ...params }) => {
                const hasFile = params.file instanceof File;
                
                if (hasFile) {
                    // Use FormData for file uploads
                    const formData = new FormData();
                    
                    if (params.name !== undefined) {
                        formData.append('name', params.name);
                    }
                    if (params.file) {
                        formData.append('file', params.file);
                    }

                    return {
                        url: `/attachments/${id}/`,
                        method: 'PATCH',
                        body: formData,
                    };
                } 
                // Use JSON for metadata updates
                return {
                    url: `/attachments/${id}/`,
                    method: 'PATCH',
                    body: JSON.stringify(params),
                    headers: {
                        'Content-Type': 'application/json',
                    },
                };
            },
            invalidatesTags: (result, error, { id }) => [
                { type: 'Attachment', id },
                { type: 'Attachment', id: 'LIST' },
            ],
        }),

        // Delete attachment
        deleteAttachment: builder.mutation<void, number>({
            query: (id) => ({
                url: `/attachments/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, id) => [
                { type: 'Attachment', id },
                { type: 'Attachment', id: 'LIST' },
            ],
        }),

        // Reorder attachments
        reorderAttachments: builder.mutation<Attachment[], ReorderAttachmentsParams>({
            query: (params) => ({
                url: '/attachments/reorder/',
                method: 'POST',
                body: JSON.stringify(params),
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            invalidatesTags: [{ type: 'Attachment', id: 'LIST' }],
        }),

        // Get document exhibits (special endpoint)
        getDocumentExhibits: builder.query<DocumentExhibit[], number>({
            query: (documentId) => ({
                url: `/attachments/document/${documentId}/exhibits/`,
                method: 'GET',
            }),
            providesTags: (result, error, documentId) => [
                { type: 'Attachment', id: `DOCUMENT_${documentId}` },
            ],
        }),
    }),
});

export default attachmentsAdminApi; 